// Ported from Fomantic UI — Segment element.
// Source: https://github.com/fomantic/Fomantic-UI (src/definitions/elements/segment.less)
// License: MIT — Copyright (c) 2013-present Fomantic-UI Team / Semantic Org.
// Variant set (raised, stacked, piled, vertical, padded, compact, attached,
// inverted, placeholder, colored, segments group, horizontal) matches the
// upstream module 1:1. CSS was hand-translated to vanilla + ry-ui tokens.

/**
 * @component ry-segment
 * @category  display
 * @summary   A bordered, padded content box — Fomantic UI's `.ui.segment`. Lighter than ry-card (thinner border, subtler shadow, no hover/interactive chrome) and ADDS two things ry-card does not: `attached` (flatten one or both edges so segments butt against adjacent surfaces with no seam) and the `<ry-segments>` group wrapper (collapses inter-segment borders + radii into one connected stack or row). Pure styling primitive — no JS state, no events.
 *
 * Reference: Fomantic UI `.ui.segment`. Every Fomantic segment variant class
 * maps to an attribute here: `raised`, `stacked`, `piled`, `vertical`,
 * `padded`, `compact`, `attached`, `inverted`, `placeholder`, and `color`.
 * The grouping behavior of `.ui.segments` lives on the sibling `<ry-segments>`.
 *
 * @attribute raised       boolean              default=false   Lifted look — a stronger drop shadow as if floating above the page.
 * @attribute stacked      boolean              default=false   Looks like a short stack of pages: a single page-edge line is drawn below the box via ::after. Adds bottom padding to leave room for the edge.
 * @attribute piled        boolean              default=false   Looks like scattered sheets of paper: two rotated copies (::before −1.2deg, ::after +1.2deg) sit behind the box. Needs vertical margin so the rotated corners are not clipped.
 * @attribute vertical     boolean              default=false   Borderless, radius-less segment with only a top divider line — meant for stacking inside a column without box chrome. First vertical segment drops its top line.
 * @attribute padded       ''|"very"            default=(unset)  Extra interior padding. Bare `padded` ≈ 1.5em; `padded="very"` ≈ 3em.
 * @attribute compact      boolean              default=false   Shrinks to fit its content instead of filling the row (display: table).
 * @attribute attached     ''|"top"|"bottom"    default=(unset)  THE headline feature. Flattens edges + removes margin so the segment connects seamlessly to an adjacent element. Bare `attached` flattens BOTH top and bottom (a middle piece). `attached="top"` keeps the top radius, flattens the bottom. `attached="bottom"` keeps the bottom radius, flattens the top. Attached segments are pulled 1px wider on each side (margin: 0 -1px) so their borders overlap the neighbor's exactly.
 * @attribute inverted     boolean              default=false   Dark surface with light text. Pairs with `color` for a solid-color inverted segment.
 * @attribute placeholder  boolean              default=false   Centered empty-state look: muted inset background, min-height, content centered both axes. Use for "no results / add your first item" panels.
 * @attribute color        string?              default=(none)   Colored top-border accent (Fomantic's colored segment). Value is a token name: primary, secondary, red, orange, yellow, olive, green, teal, blue, violet, purple, pink, brown, grey, black, success, warning, danger, info. With `inverted`, the whole surface takes the color instead of just the top border.
 *
 * @content  Any HTML — headings, paragraphs, lists, forms, other ry-ui components. The segment is a pure container and applies no transform to its children.
 *
 * @dom
 *   ry-segment[…variant attrs]
 *     > {arbitrary children}
 *     (stacked draws ::after; piled draws ::before + ::after — no element children injected)
 *
 * @gotcha  Attached-edge behavior is asymmetric and easy to get backwards:
 *          `attached="top"` means "this is the TOP piece" → it keeps its TOP
 *          corners rounded and FLATTENS its BOTTOM so the next piece can butt
 *          against it. `attached="bottom"` is the mirror. Bare `attached`
 *          (no value) flattens BOTH edges — it is a middle piece. Pattern for a
 *          connected trio: top + (bare) + bottom.
 *
 * @gotcha  Attached segments set `margin: 0 -1px; width: calc(100% + 2px)`.
 *          Their borders deliberately overlap the neighbor's by 1px on each
 *          side so there is exactly ONE border line between connected pieces,
 *          not a doubled 2px line. If you wrap an attached segment in a
 *          container with `overflow: hidden`, the 1px bleed is clipped and the
 *          seam reappears — give the wrapper horizontal padding instead.
 *
 * @gotcha  `<ry-segments>` and per-segment `attached` solve the same problem
 *          two ways. Inside `<ry-segments>` the children are stripped of their
 *          own border/radius/margin automatically — do NOT also add `attached`
 *          to children of a `<ry-segments>` group, it double-applies and breaks
 *          the corner radii. Use `attached` for ad-hoc stacks (segment +
 *          non-segment elements like an input or a button-group); use
 *          `<ry-segments>` when every piece is a segment.
 *
 * @gotcha  `piled` needs vertical breathing room. The rotated ::before/::after
 *          extend past the box; the component applies `margin: 3em 0` so they
 *          are not clipped by tight neighbors. Inside a flush layout the rotated
 *          corners can still collide with adjacent content — give piled
 *          segments space.
 *
 * @gotcha  This element has NO behavior. It sets no role, no tabindex, no
 *          listeners, fires no events. It is a styling hook only. For a
 *          clickable container use `<ry-card interactive>`.
 *
 * @example basic
 * ```html
 * <ry-segment>
 *   <p>A plain bordered, padded box. Lighter than a card.</p>
 * </ry-segment>
 * ```
 *
 * @example raised
 * ```html
 * <ry-segment raised>
 *   <p>Raised segment — lifted off the page with a stronger shadow.</p>
 * </ry-segment>
 * ```
 *
 * @example stacked
 * ```html
 * <ry-segment stacked>
 *   <p>Stacked — looks like a few pages piled neatly.</p>
 * </ry-segment>
 * ```
 *
 * @example piled
 * ```html
 * <ry-segment piled>
 *   <p>Piled — looks like loose sheets of paper scattered behind.</p>
 * </ry-segment>
 * ```
 *
 * @example padded
 * ```html
 * <ry-segment padded="very">
 *   <p>Very padded segment with generous interior spacing.</p>
 * </ry-segment>
 * ```
 *
 * @example compact
 * ```html
 * <ry-segment compact>
 *   <p>Compact — shrinks to fit its content.</p>
 * </ry-segment>
 * ```
 *
 * @example attached-group
 * ```html
 * <ry-segment attached="top">
 *   <strong>Top piece</strong> — rounded top, flat bottom.
 * </ry-segment>
 * <ry-segment attached>
 *   <strong>Middle piece</strong> — both edges flat.
 * </ry-segment>
 * <ry-segment attached="bottom">
 *   <strong>Bottom piece</strong> — flat top, rounded bottom.
 * </ry-segment>
 * ```
 *
 * @example inverted
 * ```html
 * <ry-segment inverted>
 *   <p>Inverted — dark surface, light text.</p>
 * </ry-segment>
 * ```
 *
 * @example colored
 * ```html
 * <ry-segment color="blue">
 *   <p>Colored — accent stripe along the top border.</p>
 * </ry-segment>
 * ```
 *
 * @example placeholder
 * ```html
 * <ry-segment placeholder>
 *   <p>No results yet.</p>
 * </ry-segment>
 * ```
 *
 * @example with-card-in-a-grid
 * ```html
 * <!-- Segment vs card, side by side in a ry-grid. Use a SEGMENT for a light
 *      bordered surface that groups content (and can attach to neighbours);
 *      use a CARD for a heavier, self-contained block with its own chrome. -->
 * <ry-grid cols="2">
 *   <ry-segment raised>
 *     <h4>Segment</h4>
 *     <p>Lightweight surface — thin border, subtle shadow. Good for grouping
 *        a section of a page or stacking attached pieces.</p>
 *   </ry-segment>
 *   <ry-card>
 *     <h4>Card</h4>
 *     <p>Heavier container with its own padding, radius, and elevation —
 *        a standalone content block.</p>
 *   </ry-card>
 * </ry-grid>
 * ```
 *
 * @tutorial basic-render
 * ```js
 * // A bare segment renders as a styling-only block — no role, no tabindex,
 * // no listeners. It is purely a visual container.
 * const el = mount('<ry-segment>hello</ry-segment>');
 * await expectEqual(el.tagName.toLowerCase(), 'ry-segment', 'custom element upgraded');
 * await expectEqual(el.getAttribute('role'), null, 'no role set');
 * await expectEqual(el.getAttribute('tabindex'), null, 'no tabindex set');
 * await expectEqual(el.textContent, 'hello', 'children preserved verbatim');
 * ```
 *
 * @tutorial variant-attributes-reflect
 * ```js
 * // Every variant is a plain attribute the CSS reads — the component does
 * // not rewrite or normalize them, so what you set is what queries see.
 * const el = mount('<ry-segment raised stacked padded="very">x</ry-segment>');
 * await expectEqual(el.hasAttribute('raised'), true, 'raised present');
 * await expectEqual(el.hasAttribute('stacked'), true, 'stacked present');
 * await expectAttr(el, 'padded', 'very', 'padded value preserved');
 * ```
 *
 * @tutorial attached-top-bottom-both
 * ```js
 * // Attached is asymmetric: top = rounded-top/flat-bottom, bottom = mirror,
 * // bare attached = both edges flat (a middle piece). The attribute VALUE
 * // is the surface the piece is the TOP/BOTTOM of, not the flat edge.
 * const top = mount('<ry-segment attached="top">t</ry-segment>');
 * await expectAttr(top, 'attached', 'top', 'top piece');
 * const bottom = mount('<ry-segment attached="bottom">b</ry-segment>');
 * await expectAttr(bottom, 'attached', 'bottom', 'bottom piece');
 * const middle = mount('<ry-segment attached>m</ry-segment>');
 * await expectEqual(middle.getAttribute('attached'), '', 'bare attached = empty string value = both edges flat');
 * ```
 *
 * @tutorial inverted-toggle
 * ```js
 * // inverted is a boolean attribute — present means dark surface, absent
 * // means default. Symmetric proof of both directions.
 * const dark = mount('<ry-segment inverted>x</ry-segment>');
 * await expectEqual(dark.hasAttribute('inverted'), true, 'inverted on = dark surface');
 * const light = mount('<ry-segment>x</ry-segment>');
 * await expectEqual(light.hasAttribute('inverted'), false, 'inverted off = default surface');
 * ```
 *
 * @tutorial segments-group-renders-children
 * ```js
 * // <ry-segments> is a group wrapper. It collapses inter-segment borders and
 * // radii via CSS — it does NOT move, clone, or transform its children. The
 * // child segments are the same elements, just visually joined.
 * const group = mount(`
 *   <ry-segments>
 *     <ry-segment>one</ry-segment>
 *     <ry-segment>two</ry-segment>
 *     <ry-segment>three</ry-segment>
 *   </ry-segments>
 * `);
 * const kids = group.querySelectorAll('ry-segment');
 * await expectEqual(kids.length, 3, 'all three child segments present and untouched');
 * await expectEqual(kids[0].textContent, 'one', 'first child intact');
 * ```
 *
 * @tutorial horizontal-group
 * ```js
 * // The group lays children top-to-bottom by default. [horizontal] switches
 * // to a left-to-right row; the attribute is the only thing that changes —
 * // children are identical.
 * const row = mount(`
 *   <ry-segments horizontal>
 *     <ry-segment>L</ry-segment>
 *     <ry-segment>R</ry-segment>
 *   </ry-segments>
 * `);
 * await expectEqual(row.hasAttribute('horizontal'), true, 'horizontal flag set');
 * await expectEqual(row.querySelectorAll('ry-segment').length, 2, 'children intact in row layout');
 * ```
 *
 * @see ry-card
 * @see ry-button-group
 */
export class RySegment extends HTMLElement {
  // Pure styling primitive. No setup, no listeners, no state. The presence of
  // a custom-element class (rather than leaving the tag undefined) lets the
  // transform system and TypeScript treat it as a first-class component, and
  // gives consumers a stable constructor to instanceof against.
}

customElements.define('ry-segment', RySegment);

/**
 * @component ry-segments
 * @category  display
 * @summary   Group wrapper for `<ry-segment>` children — Fomantic UI's `.ui.segments`. Strips the inner border, radius, and margin off each child so a column (default) or row (`horizontal`) of segments reads as ONE connected, multi-pane surface with a single outer border and shared rounded corners. The outermost top/bottom (or left/right) corners stay rounded; everything between is flat.
 *
 * Reference: Fomantic UI `.ui.segments`. `horizontal` and `raised` map to the
 * same-named upstream modifier classes.
 *
 * @attribute horizontal  boolean   default=false   Lay child segments left-to-right in a flex row instead of stacking them top-to-bottom. Internal dividers become vertical (left borders); first/last children round the left/right ends.
 * @attribute raised      boolean   default=false   Lifted shadow on the whole group, matching `<ry-segment raised>`.
 *
 * @content  Direct `<ry-segment>` children (and, per Fomantic, nested
 *           `<ry-segments>` for sub-grouping). Non-segment children are not
 *           styled by the group.
 *
 * @dom
 *   ry-segments[horizontal?][raised?]
 *     > ry-segment (first — keeps outer-leading corners, loses inner border)
 *     > ry-segment (middle — flat all around, single divider line)
 *     > ry-segment (last — keeps outer-trailing corners)
 *
 * @gotcha  Do NOT put `attached` on segments inside `<ry-segments>`. The group
 *          already removes their borders/radii; adding `attached` double-applies
 *          the radius suppression and the outer corners go square.
 *
 * @gotcha  A group with a SINGLE child segment keeps all four corners rounded
 *          (`:only-child`). Connected-stack styling only kicks in with two or
 *          more children.
 *
 * @gotcha  `horizontal` changes which edges divide. In a vertical group the
 *          divider is a top border on each non-first child; in a horizontal
 *          group it is a left border. Mixing assumptions (e.g. expecting a top
 *          line in a horizontal group) is the usual surprise.
 *
 * @example basic-stack
 * ```html
 * <ry-segments>
 *   <ry-segment>Top</ry-segment>
 *   <ry-segment>Middle</ry-segment>
 *   <ry-segment>Bottom</ry-segment>
 * </ry-segments>
 * ```
 *
 * @example horizontal
 * ```html
 * <ry-segments horizontal>
 *   <ry-segment>Left</ry-segment>
 *   <ry-segment>Center</ry-segment>
 *   <ry-segment>Right</ry-segment>
 * </ry-segments>
 * ```
 *
 * @example raised
 * ```html
 * <ry-segments raised>
 *   <ry-segment>One</ry-segment>
 *   <ry-segment>Two</ry-segment>
 * </ry-segments>
 * ```
 *
 * @tutorial group-joins-children
 * ```js
 * // The group is CSS-only joinery — children are untouched in the DOM.
 * const group = mount(`
 *   <ry-segments>
 *     <ry-segment>a</ry-segment>
 *     <ry-segment>b</ry-segment>
 *   </ry-segments>
 * `);
 * await expectEqual(group.children.length, 2, 'children remain direct DOM children');
 * await expectEqual(group.children[0].tagName.toLowerCase(), 'ry-segment', 'first child is a segment');
 * ```
 *
 * @tutorial horizontal-and-raised-flags
 * ```js
 * // Both group modifiers are boolean attributes read by CSS. Proof they
 * // reflect and can combine.
 * const el = mount('<ry-segments horizontal raised><ry-segment>x</ry-segment></ry-segments>');
 * await expectEqual(el.hasAttribute('horizontal'), true, 'horizontal set');
 * await expectEqual(el.hasAttribute('raised'), true, 'raised set');
 * ```
 *
 * @see ry-segment
 */
export class RySegments extends HTMLElement {
  // Pure styling primitive — joins child segments via CSS only.
}

customElements.define('ry-segments', RySegments);
